import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import CartToast from "@/components/storefront/CartToast";
import { I18nProvider } from "@/lib/i18n";
import { TryOnLauncherProvider } from "@/lib/tryonLauncher";
import { TryonJobProvider } from "@/lib/tryonJobs";
import TryonResultDock from "@/components/atelier/TryonResultDock";
import Home from "@/pages/Home";
import PDP from "@/pages/PDP";
import TryOnPage from "@/pages/TryOnPage";
import { CollectionDetail } from "@/pages/Collections";
import Gallery from "@/pages/Gallery";
import Category from "@/pages/Category";
import CartPage from "@/pages/Cart";
import Activate from './pages/Activate';
import ResetPassword from './pages/ResetPassword';
import Checkout from "@/pages/Checkout";
import { CheckoutSuccess, CheckoutCancelled } from "@/pages/CheckoutResult";
import Account from "@/pages/Account";
import OrderTracking from "@/pages/OrderTracking";
import Auth from "@/pages/Auth";
import { JournalIndex, JournalArticlePage } from "@/pages/Journal";
import TagPage from "@/pages/TagPage";
import {
  DeliveryPage,
  PrivacyPage,
  ReturnsPage,
  TermsPage,
} from "@/pages/Policies";
import Shop from "@/pages/Shop";
import OutletPage from "./pages/Outlet";
import NewArrivalsPage from "./pages/NewArrivals";
import AboutPage from "./pages/About";
import ContactPage from "./pages/Contact";
import EndirimPage from "./pages/Endirim";
import GiftCardPage from "./pages/GiftCard";
import NotFound from "@/pages/NotFound";
import ScrollToTop from "@/components/layout/ScrollToTop";
import AnalyticsRouteTracker from "@/components/layout/AnalyticsRouteTracker";
import MobileBottomNavigation from "@/components/layout/MobileBottomNavigation";
import { FalconSocietyLanding } from "@/pages/FalconSociety";

function CategoryRedirect() {
  const { handle } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/categories/${handle}${search}`} replace />;
}


/* Clears the caught error whenever the route changes, so walking away from a
   broken page recovers without a reload. Inside the router, because it needs
   the location. */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <I18nProvider>
          <BrowserRouter>
            <TryonJobProvider>
            <TryOnLauncherProvider>
              <ScrollToTop />
              <AnalyticsRouteTracker />
              <RouteErrorBoundary>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              {/* Named OutletPage: `Outlet` is react-router-dom's own export. */}
              <Route path="/outlet" element={<OutletPage />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/endirim" element={<EndirimPage />} />
              <Route path="/sale" element={<Navigate to="/endirim" replace />} />
              <Route path="/products/:handle" element={<PDP />} />
              <Route path="/try-on" element={<TryOnPage />} />
              {/* Kolleksiyalar bəndi və səhifəsi ləğv olundu (tapşırıq 2026-10-05):
                  köhnə link ana səhifəyə yönlənir. Serverdə 301 nginx-dədir;
                  bu, SPA daxilindəki keçidlər üçündür. Tək kolleksiya
                  səhifələri (/collections/:handle) işləməyə davam edir. */}
              <Route path="/collections" element={<Navigate to="/" replace />} />
              {/* «Yeni gələnlər» kolleksiya deyil, bayraq siyahısıdır — xüsusi
                  marşrut generik kolleksiyadan ƏVVƏL durmalıdır. */}
              <Route path="/collections/new-arrivals" element={<NewArrivalsPage />} />
              <Route path="/collections/:handle" element={<CollectionDetail />} />
              {/* One canonical URL for a category. Both spellings served the
                  same page, so internal links split their value between two
                  addresses; the canonical tag resolved it for crawlers but the
                  singular form stayed live. It redirects now, which also keeps
                  analytics from reporting one shelf as two. */}
              <Route path="/category/:handle" element={<CategoryRedirect />} />
              <Route path="/categories/:handle" element={<Category />} />
              <Route path="/gift-card" element={<GiftCardPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/activate" element={<Activate />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancelled" element={<CheckoutCancelled />} />
              <Route path="/account" element={<Account />} />
              <Route path="/orders/:id" element={<OrderTracking />} />
              <Route path="/auth/login" element={<Auth mode="login" />} />
              <Route path="/auth/register" element={<Auth mode="register" />} />
              <Route path="/blog" element={<JournalIndex />} />
              <Route path="/blog/:slug" element={<JournalArticlePage />} />
              <Route path="/journal" element={<JournalIndex />} />
              <Route path="/journal/:slug" element={<JournalArticlePage />} />
              <Route path="/tags/:slug" element={<TagPage />} />
              <Route path="/vip" element={<Navigate to="/falcon-society" replace />} />
              <Route path="/falcon-society" element={<FalconSocietyLanding />} />
              <Route path="/account/falcon" element={<Navigate to="/account?tab=loyalty" replace />} />
              <Route path="/account/falcon/benefits" element={<Navigate to="/account?tab=benefits" replace />} />
              <Route path="/account/falcon/partners/:slug" element={<Navigate to="/account?tab=benefits" replace />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/delivery" element={<DeliveryPage />} />
              <Route path="/returns" element={<ReturnsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="*" element={<NotFound />} />
              </Routes>
              </RouteErrorBoundary>
              <MobileBottomNavigation />
              <CartToast />
            <TryonResultDock />
            </TryOnLauncherProvider>
            </TryonJobProvider>
          </BrowserRouter>
        </I18nProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
